import * as d3 from 'd3';
import { select as d3Select } from 'd3-selection';
import { memo, useLayoutEffect } from 'react';
// Tools
import { no_propagate } from './utils';
// Custom hooks
import useAnimated from './useAnimated';
// Constants
import * as constants from './constants';

const NODE_CONNECTION_PADDING = 3;

const ribbonGenerator = d3.ribbon();

const polar_to_cartesian = (r, theta) => {
  // theta is measured from 12 o'clock and in a clockwise direction
  return [r * Math.sin(theta), r * -Math.cos(theta)];
}

const interpolate_ribbon = (path_attrs) => {
  const source_start_point = polar_to_cartesian(path_attrs.source.radius, path_attrs.source.startAngle);
  const source_end_point = polar_to_cartesian(path_attrs.source.radius, path_attrs.source.endAngle);
  const target_start_point = polar_to_cartesian(path_attrs.target.radius, path_attrs.target.startAngle);
  const target_end_point = polar_to_cartesian(path_attrs.target.radius, path_attrs.target.endAngle);

  const interp = (a, b, t) => {
    return [(1 - t)*a[0] + t*b[0], (1 - t)*a[1] + t*b[1]];
  }

  const interpolate_quadratic = (start_point, end_point, t) => {
    const interp_start = interp(start_point, [0,0], t);
    const interp_end = interp([0,0], end_point, t);
    return interp(interp_start, interp_end, t);
  }

  return (t) => {
    if (t === 1) {
      // Return the real final ribbon, which uses elliptical ends
      return ribbonGenerator(path_attrs);
    }
    // First arc (A) is from source_end_point to target_start_point.
    // End point interpolates quadratically from source_end_point to target_start_point
    const point_A = interpolate_quadratic(source_end_point, target_start_point, t);
    // Control point interpolates linearly from source_end_point to origin
    const ctrl_A = interp(source_end_point, [0,0], t);

    // First arc (B) is from source_start_point to target_end_point.
    // End point interpolates quadratically from source_start_point to target_end_point
    const point_B = interpolate_quadratic(source_start_point, target_end_point, t);
    // Control point interpolates linearly from source_start_point to origin
    const ctrl_B = interp(source_start_point, [0,0], t);

    // Start at source_start_point
    let path = `M${source_start_point[0]},${source_start_point[1]}`;
    // Line (should be elliptical segment but use line for now) to source_end_point
    path += `L${source_end_point[0]},${source_end_point[1]}`;
    // Quadratic to point_A with ctrl point ctrl_A
    path += `Q${ctrl_A[0]},${ctrl_A[1]},${point_A[0]},${point_A[1]}`;
    // Line to point_B
    path += `L${point_B[0]},${point_B[1]}`;
    // Quadratic to source_start_point with ctrl point ctrl_B, and end.
    path += `Q${ctrl_B[0]},${ctrl_B[1]},${source_start_point[0]},${source_start_point[1]}Z`;
    return path;
  }
}

const TripRibbon = memo(({ radius, source, target, fill, focused, trip_index, selected_person_change, initial_anim_delay, set_focused_trip, set_selected_trip }) => {

  const path_attrs = {
    source: {
      startAngle: source.start_angle,
      endAngle: source.end_angle,
      radius: radius - constants.NODE_THICKNESS - NODE_CONNECTION_PADDING
    },
    target: {
      startAngle: target.start_angle,
      endAngle: target.end_angle,
      radius: radius - constants.NODE_THICKNESS - NODE_CONNECTION_PADDING
    }
  };

  const new_path = ribbonGenerator(path_attrs);
  const opacity = focused ? "1" : "0.05";

  const [ref, {d}] = useAnimated({
    attrs : {d : new_path},
    duration : selected_person_change ? constants.SELECTION_ANIM_DURATION : constants.REORDER_ANIM_DURATION,
    deps : [new_path]
  });

  const set_focused_this_trip = () => set_focused_trip(trip_index);
  const set_unhighlighted_trip = () => set_focused_trip(null);
  const set_selected_this_trip = () => set_selected_trip(trip_index);

  // Initial animation
  useLayoutEffect(() => {
    const tween = interpolate_ribbon(path_attrs);
    d3Select(ref.current)
      .attr('opacity', 1)
      .attr('d', tween(0))
      .transition()
      .duration(constants.RIBBON_INITIAL_ANIM_DURATION)
      .delay(constants.RIBBON_INITIAL_ANIM_DELAY_MIN + initial_anim_delay * constants.RIBBON_INITIAL_ANIM_DELAY_RANGE)
      .attr('opacity', opacity)
      .attrTween('d', () => interpolate_ribbon(path_attrs))
  }, []);

  return (
    <path ref={ref}
          d={d}
          fill={fill}
          data-trip_index={trip_index}
          opacity={opacity}
          onMouseEnter={set_focused_this_trip}
          onMouseLeave={set_unhighlighted_trip}
          onClick={no_propagate(set_selected_this_trip)}/>
  );
});

export default TripRibbon;
